<template>
  <b-card v-if="show" v-loading="loadingText !== ''" :element-loading-text="loadingText">
    <h4>
      Billing Details
      <div class="pull-right">
        <a href="https://stripe.com/ca" class="pull-right" target="_blank" rel="noreferrer nofollow"><img class="powered-by" src="/img/payments/powered_by_stripe.png" /></a>
      </div>
    </h4>
    <b-alert :show="success" dismissible fade variant="success" class="mt-2" @dismissed="success = false" @dismiss-count-down="(i) => success = i">Billing details updated successfully!</b-alert>
    <b-form class="mt-3">
      <div v-if="updating">
        <b-form-group>
          <label>Card Number</label>
          <card-number class="card-element" :stripe="stripeKey" :options="{ hidePostalCode: true }" />
        </b-form-group>
        <b-row>
          <b-col :xs="8">
            <b-form-group>
              <label>Expiry Date</label>
              <card-expiry class="card-element  " :stripe="stripeKey" />
            </b-form-group>
          </b-col>
          <b-col :xs="8">
            <b-form-group>
              <label>CVV</label>
              <card-cvc class="card-element " :stripe="stripeKey" />
            </b-form-group>
          </b-col>
        </b-row>
        <div>
          <b-button variant="primary" @click="updateBilling()">Save</b-button>
          <b-button variant="link" class="text-danger" @click="updating = false">Cancel</b-button>
        </div>
      </div>
      <b-row v-else>
        <b-col :xl="8">
          <b-card class="credit-card mb-0">
            <header slot="header" :class="brandKey">
              <img v-if="brandKey !== 'unknown'" :src="`/img/payments/${brandKey}.svg`" :alt="billing.brand" class="card-logo" />
              <span v-if="brandKey === 'unknown' || brandKey === 'mastercard'">{{billing.brand}}</span>
              <span class="pull-right">
                <b-button variant="link" class="text-white p-0" size="sm" @click="updating = true"><i class="icon-pencil"></i></b-button>
              </span>
            </header>
            <div class="number">
              <span>&bull;&bull;&bull;&bull;</span>
              <span v-if="brandKey !== 'amex'">&bull;&bull;&bull;&bull;</span>
              <span v-else>&bull;&bull;&bull;&bull;&bull;&bull;</span>
              <span v-if="brandKey !== 'amex'">&bull;&bull;&bull;&bull;</span>
              <span v-else>&bull;&bull;&bull;&bull;&bull;</span>
              <span>{{billing.lastFour}}</span>

              <div class="expiry">
                {{billing.expMonth < 10 ? '0' : ''}}{{billing.expMonth}} / {{billing.expYear}}
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <p class="small mt-3 mb-0">All payments are processed by Stripe, a world-leader in online payments that represents brands like Twitter, Pinterest, Kickstarter and Shopify. <strong>We do not store any of your payment information</strong> &mdash; Stripe securely handles all of that. We are simply notified when a payment is successful or if a payment fails. Feel free to <a href="https://stripe.com/ca" target="_blank" rel="noreferrer nofollow">visit Stripe</a> to learn more about them and their security standards.</p>
      <b-alert :show="!!error" fade variant="danger" class="mt-4 mb-0">{{error}}</b-alert>
    </b-form>
  </b-card>
</template>

<script>
import {
  CardNumber,
  CardExpiry,
  CardCvc,
  createToken
} from "vue-stripe-elements-plus";

const LoadingText = "Loading Billing Details...";
const UpdatingText = "Updating Billing Details...";

export default {
  name: "AccountBilling",

  components: {
    CardNumber,
    CardExpiry,
    CardCvc
  },

  data() {
    return {
      billing: {},

      // State
      show: this.$session.get("account.subscribed"),
      updating: false,
      stripeKey: process.env.VUE_APP_STRIPE_KEY,
      loadingText: LoadingText,
      success: false,
      error: ""
    };
  },

  mounted() {
    this.load();
  },

  computed: {
    brandKey() {
      switch (String(this.billing.brand).toLowerCase()) {
        case "visa":
          return "visa";
        case "american express":
          return "amex";
        // case 'Discover':
        //   return ''
        // case 'Diners Club':
        //   return ''
        // case 'JCB':
        //   return ''
        case "mastercard":
          return "mastercard";
        // case 'UnionPay':
        //   return ''
        default:
          return "unknown";
      }
    }
  },

  methods: {
    load() {
      this.loadingText = LoadingText;
      return this.$API.v2.Account.BillingInfo()
        .then(billing => {
          this.billing = billing;
          this.loadingText = "";
        })
        .catch(err => {
          this.error = err;
          this.loadingText = "";
        });
    },

    updateBilling() {
      this.loadingText = UpdatingText;
      this.error = "";
      createToken()
        .then(({ token, error }) => {
          if (error) {
            throw error.message;
          }

          return this.$API.v2.Account.UpdateBillingInfo(token.id);
        })
        .then(billing => {
          this.billing = billing;
          this.updating = false;
          this.success = 5;
          this.loadingText = "";
        })
        .catch(err => {
          this.error = err;
          this.loadingText = "";
        });
    },

    subscriptionChanged(subscription) {
      this.show = subscription !== false;
      if (this.show) {
        this.load();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.credit-card {
  border: 0;

  .card-header {
    padding: 0;

    header {
      padding: 0.5rem 0.75rem;
      color: white;

      &.visa {
        background-color: #224dba;
      }

      &.mastercard {
        background-color: #353a48;
      }

      &.amex {
        background: #2c6da6;
      }

      // &.discover {
      //   background: #e3793c;
      // }

      // &.jcb {
      //   background: #0d3f95;
      // }

      > span {
        display: inline-block;
        margin-left: 0.5rem;
      }
    }
  }

  .card-body {
    padding: 1rem;
    border: 1px solid #c8ced3;
    border-top: 0;
  }

  .card-logo {
    max-width: 40px;
  }

  .number {
    font-family: monospace;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.8px;

    > span {
      display: inline-block;
      margin-right: 0.5rem;

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  .expiry {
    font-family: monospace;
    color: #a7a7a7;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.8px;
    float: right;
  }
}

.powered-by {
  height: 30px;
}

.card-element {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #5c6873;
  border: 1px solid #e4e7ea;
  border-radius: 0.25rem;
}
</style>
